/** Helpers de formato para monedas y fechas relativas en el PWA. */

const CURRENCY_SYMBOLS: Record<string, string> = {
  PEN: 'S/',
  USD: '$',
  EUR: '€',
};

/**
 * Formatea un valor numérico como moneda.
 * Ejemplos: 1234.5, 'PEN' → "S/ 1,234.50"
 *           1234.5, 'USD' → "$ 1,234.50"
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currency: string = 'PEN',
): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || Number.isNaN(num)) return '—';
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const formatted = num.toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol} ${formatted}`;
}

/**
 * Convierte una fecha ISO a texto relativo legible.
 * "2026-10-14T10:00:00Z" → "hace 2 horas" / "hoy" / "mañana" / "en 3 días"
 */
export function formatRelativeDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  // Hoy
  if (diffDays === 0) {
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    if (Math.abs(diffHours) < 1) return 'ahora';
    if (diffHours > 0) return `en ${diffHours} hora${diffHours === 1 ? '' : 's'}`;
    const absH = Math.abs(diffHours);
    return `hace ${absH} hora${absH === 1 ? '' : 's'}`;
  }

  // Ayer
  if (diffDays === -1) return 'ayer';
  // Mañana
  if (diffDays === 1) return 'mañana';

  // Pasado reciente (hasta 6 días)
  if (diffDays < 0 && diffDays >= -6) {
    return `hace ${Math.abs(diffDays)} días`;
  }

  // Futuro cercano (hasta 6 días)
  if (diffDays > 1 && diffDays <= 6) {
    return `en ${diffDays} días`;
  }

  // Fechas más lejanas: formato dd/mm/yyyy
  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Indica si una fecha está vencida (pasado). */
export function isOverdue(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}
